import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from 'utils/api';

const baseURL = '/format';

// Fetch all formats
export const fetchFormats = createAsyncThunk('format/fetchFormats', async (_, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  try {
    const token = localStorage.getItem('token');
    const res = await api.get(`${baseURL}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data.data;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

// Fetch formats by project ID
export const fetchFormatByProjectId = createAsyncThunk(
  'format/fetchFormatByProjectId',
  async (projectId, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`${baseURL}/byProjectId/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create a single format
export const createFormat = createAsyncThunk(
  'format/createFormat',
  async ({ id, ...formatData }, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const res = await api.post(
        baseURL,
        { ...formatData, project_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update a single format
export const updateFormat = createAsyncThunk(
  'format/updateFormat',
  async ({ id, ...formatData }, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const res = await api.put(`${baseURL}/${id}`, formatData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete a format
export const deleteFormat = createAsyncThunk(
  'format/deleteFormat',
  async (formatId, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const response = await api.delete(`${baseURL}/${formatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ NEW: Bulk save plate formats (replaces broken updateMultipleFormats)
export const savePlateFormats = createAsyncThunk(
  'format/savePlateFormats',
  async ({ projectId, formats }, thunkAPI) => {
    const { rejectWithValue, getState } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      
      // Delete existing formats for this project
      const state = getState();
      const existingFormats = state.format.formats || [];
      
      for (const fmt of existingFormats) {
        if (fmt.id) {
          await api.delete(`${baseURL}/${fmt.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      
      // Create new formats
      const response = await api.post(
        `${baseURL}/multiple/${projectId}`,
        { formatsData: formats },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create multiple formats (initial creation)
export const createMultipleFormats = createAsyncThunk(
  'format/createMultipleFormats',
  async ({ projectId, formatsData }, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem('token');
      const res = await api.post(
        `${baseURL}/multiple/${projectId}`,
        { formatsData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const formatSlice = createSlice({
  name: 'format',
  initialState: {
    format: null,
    loading: false,
    error: null,
    formats: []
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch formats
      .addCase(fetchFormats.fulfilled, (state, action) => {
        state.loading = false;
        state.formats = action.payload || [];
      })
      .addCase(fetchFormats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by project ID
      .addCase(fetchFormatByProjectId.fulfilled, (state, action) => {
        state.loading = false;
        state.formats = action.payload || [];
      })
      .addCase(fetchFormatByProjectId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormatByProjectId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create single format
      .addCase(createFormat.fulfilled, (state, action) => {
        state.loading = false;
        state.formats.push(action.payload);
        state.format = action.payload;
      })
      .addCase(createFormat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createFormat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update single format
      .addCase(updateFormat.fulfilled, (state, action) => {
        state.loading = false;
        state.formats = state.formats.map((format) =>
          format.id === action.payload.id ? action.payload : format
        );
      })
      .addCase(updateFormat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFormat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete format
      .addCase(deleteFormat.fulfilled, (state, action) => {
        state.loading = false;
        state.formats = state.formats.filter((format) => format.id !== action.payload.id);
      })
      .addCase(deleteFormat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFormat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ✅ Bulk save formats
      .addCase(savePlateFormats.fulfilled, (state, action) => {
        state.loading = false;
        state.formats = action.payload || [];
      })
      .addCase(savePlateFormats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(savePlateFormats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create multiple formats
      .addCase(createMultipleFormats.fulfilled, (state, action) => {
        state.loading = false;
        state.formats.push(...(action.payload || []));
      })
      .addCase(createMultipleFormats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMultipleFormats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export default formatSlice.reducer;